import { fetchAuthSession, signOut } from "aws-amplify/auth";
import { cognitoUserPoolsTokenProvider } from "aws-amplify/auth/cognito";
import Cookies from "universal-cookie";

import { getConfigs } from "app/utils/configs";
import { localDevEnabled } from "app/utils/localDev";

export interface CookieOptions {
  domain: string;
  path: string;
  secure: boolean;
  sameSite: "strict" | "lax" | "none";
}

export function getAuthCookieOptions(): CookieOptions {
  return {
    domain:
      window.location.hostname.split(".").length > 2
        ? window.location.hostname.replace(/^[^.]*/, "")
        : window.location.hostname,
    path: "/",
    secure: window.location.protocol === "https:",
    // Lax still lets a top-level SSO navigation into a sibling service carry the
    // cookie, while blocking it from being sent on cross-site subrequests.
    sameSite: "lax",
  };
}

/** The Cognito tokens `fetchAuthSession()` resolves for a signed-in user. */
export type AuthTokens = NonNullable<
  Awaited<ReturnType<typeof fetchAuthSession>>["tokens"]
>;

/**
 * The current tokens, signing the user out if there are none.
 *
 * v5's `currentSession()` rejected for a signed-out user; v6's
 * `fetchAuthSession()` resolves without tokens instead, and still rejects when
 * a refresh fails outright. Both mean the session is gone, so both sign out —
 * which emits `signedOut`, and that is what drops the Authenticator back to
 * the sign-in screen.
 */
async function getSession(): Promise<AuthTokens | null> {
  try {
    const { tokens } = await fetchAuthSession();
    if (tokens) {
      return tokens;
    }
  } catch {
    // Treated the same as no tokens.
  }
  signOut().catch(() => {
    // Nothing left to clear; the caller already gets "Unauthenticated".
  });
  return null;
}

// Local-development backends (platform's `just local-console`, see
// app/utils/localDev) skip the Cognito sign-in but still want a real JWT so the
// cross-service validation path runs.  The local sign-in form (LocalIdpLogin)
// records which seeded user to act as; we mint that user's token straight from
// the local IdP (via the vite /api/local-token proxy in local-dev mode) and reuse it.
const LOCAL_IDP_USER_KEY = "stacklet-local-idp-user";

let localToken: { user: string; promise: Promise<string> } | null = null;

export function getLocalIdpUser(): string | null {
  return localStorage.getItem(LOCAL_IDP_USER_KEY);
}

export function setLocalIdpUser(username: string): void {
  localStorage.setItem(LOCAL_IDP_USER_KEY, username);
  localToken = null; // a different user needs a different token
}

export function clearLocalIdpUser(): void {
  localStorage.removeItem(LOCAL_IDP_USER_KEY);
  localToken = null;
  document.cookie = "stacklet-auth=; path=/; max-age=0";
  document.cookie = "stacklet-access=; path=/; max-age=0";
  document.cookie = "stacklet-refresh=; path=/; max-age=0";
}

/**
 * Sets the cross-service JWT cookies used for SSO redirects out of the
 * console: Redash reads `stacklet-auth`, Superset reads `stacklet-access`.
 * Both services run on localhost so a host-only cookie reaches them
 * regardless of port; SameSite=Lax so a top-level navigation from the
 * console carries it. The local IdP mints one token per user, so both
 * cookies get the same value here — in production (see authCookieHandler)
 * they hold the ID token and access token respectively.
 */
export function setLocalIdpCookies(token: string): void {
  document.cookie = `stacklet-auth=${token}; path=/; SameSite=Lax`;
  document.cookie = `stacklet-access=${token}; path=/; SameSite=Lax`;
}

async function getLocalToken(): Promise<string> {
  const user = getLocalIdpUser();
  if (!user) {
    // The sign-in gate renders children only once a user is chosen, so this
    // is unreachable in practice; fail soft rather than throw mid-request.
    return "Unauthenticated";
  }
  if (!localToken || localToken.user !== user) {
    const { api } = await getConfigs();
    const tokenUrl = api.local_idp_token_url;
    if (!tokenUrl) {
      throw new Error("local IdP token URL missing from deployment config");
    }
    const promise = fetch(`${tokenUrl}?username=${encodeURIComponent(user)}`)
      .then((res) => {
        if (!res.ok) {
          throw new Error(`local IdP token request failed: ${res.status}`);
        }
        return res.json();
      })
      .then((body) => {
        const token = body.access_token as string;
        setLocalIdpCookies(token);
        return token;
      })
      .catch((err) => {
        // Don't cache a failure -- let the next request retry.
        localToken = null;
        throw err;
      });
    localToken = { user, promise };
  }
  return localToken.promise;
}

/**
 * Whether the user is signed in, without `getSession`'s sign-out side effect.
 *
 * Route loaders run before the sign-in gate has decided anything, so they ask
 * this before starting a query: an unauthenticated request comes back a 401,
 * and the page would then be holding a query reference that signing in cannot
 * repair.
 */
export async function hasAuthSession(): Promise<boolean> {
  if (localDevEnabled()) {
    return getLocalIdpUser() !== null;
  }

  return fetchAuthSession().then(
    ({ tokens }) => tokens !== undefined,
    () => false,
  );
}

// The token getters accept already-resolved tokens so a caller holding them
// (e.g. authCookieHandler) can read every token from a single fetchAuthSession()
// call instead of re-fetching per token. Called without them, they resolve the
// session themselves.
export async function getAuthToken(tokens?: AuthTokens): Promise<string> {
  if (localDevEnabled()) {
    return getLocalToken();
  }

  const resolved = tokens ?? (await getSession());

  return resolved?.accessToken.toString() ?? "Unauthenticated";
}

export async function getIdToken(tokens?: AuthTokens): Promise<string> {
  const resolved = tokens ?? (await getSession());

  return resolved?.idToken?.toString() ?? "Unauthenticated";
}

/**
 * The raw refresh token, which Sinistral bootstraps its own session from.
 *
 * `fetchAuthSession()` deliberately leaves it out — the Amplify team intends
 * it for the SDK's own refreshes only (aws-amplify/amplify-js#14324) — but the
 * token store the SDK reads it from is public, exported from
 * `aws-amplify/auth/cognito`, so a change to that surface fails typecheck
 * rather than silently emptying the cookie. SSO users signed in through the
 * implicit grant never had one, in v5 either.
 */
export async function getRefreshToken(tokens?: AuthTokens): Promise<string> {
  const resolved = tokens ?? (await getSession());
  if (!resolved) {
    return "Unauthenticated";
  }

  const stored =
    await cognitoUserPoolsTokenProvider.authTokenStore.loadTokens();

  return stored?.refreshToken ?? "Unauthenticated";
}

export async function authCookieHandler() {
  const cookies = new Cookies();
  // Not getSession: that signs out when there is no session, and this runs on
  // every auth event — including `signedOut`, which would loop.
  const tokens = await fetchAuthSession().then(
    (session) => session.tokens ?? null,
    () => null,
  );

  if (tokens) {
    // We deliberately mirror only the three Cognito tokens into cookies (which
    // are always browser-readable regardless of storage backend). Keeping the
    // token set self-contained leaves us free to move the rest of Amplify's
    // session state back into localStorage.
    //
    // Pass the resolved tokens into each getter so all three come from this
    // one fetchAuthSession() call — re-fetching per token would risk a getter
    // seeing an expired/absent session and storing "Unauthenticated".
    cookies.set(
      "stacklet-auth",
      await getIdToken(tokens),
      getAuthCookieOptions(),
    );
    cookies.set(
      "stacklet-access",
      await getAuthToken(tokens),
      getAuthCookieOptions(),
    );
    cookies.set(
      "stacklet-refresh",
      await getRefreshToken(tokens),
      getAuthCookieOptions(),
    );
  } else {
    cookies.remove("stacklet-auth", getAuthCookieOptions());
    cookies.remove("stacklet-access", getAuthCookieOptions());
    cookies.remove("stacklet-refresh", getAuthCookieOptions());
  }
}
